import { flow } from 'fp-ts/function';
import { concat, get, has, isEmpty, keys, omit, reject, set, size } from 'lodash/fp';
import { createSelector } from 'reselect';
import { AccountingState, OrderItemType } from '@mews-commander/data-access';
import { getStringProp } from '@mews/fp';
import {
    composeProps,
    createAction,
    createPropsExtender,
    createScopedReducer,
    createScopedSelector,
} from '@mews/framework';
import { ApplicationState } from '@mews-commander/types';
import { notEmpty } from '@mews-commander/utils';
import {
    getOrderItemIdsByBillId,
    getOrderItemsByBillId,
    getOrderItemsByIds,
} from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { getCurrentEnterprisePaymentIdsByBillId } from '@mews-commander/legacy-entities/src/ducks/payments';
import { moduleSelector as parentSelector, prefix } from '../module';
import { getSelectableOrderItemIds } from '../rebate/selectors/orderItems';
import {
    getBillNestedOrderItemIdsByServiceOrderId,
    getGroupedOrderItemIds,
    getNestedProductServiceIds,
    getNestedServiceIds,
    getOrderItemIdsByMonth,
} from '../selectors/grouping';
import { getOrderedMonthServiceOrders } from '../selectors/orderItems';
import { getBillPaymentIdsWithServiceOrderId } from './payments';

export const NAME = 'billItems';

const TOGGLE_ORDER_ITEM_SELECT = prefix('TOGGLE_ORDER_ITEM_SELECT');
const TOGGLE_ORDER_ITEM_DESELECT = prefix('TOGGLE_ORDER_ITEM_DESELECT');
const TOGGLE_PAYMENT_SELECT = prefix('TOGGLE_PAYMENT_SELECT');
const TOGGLE_BILL_ITEMS_SELECT = prefix('TOGGLE_BILL_ITEMS_SELECT');
const DESELECT_ALL = prefix('DESELECT_ALL');

export const toggleOrderItemSelect = (orderItemId: string, scope: string) =>
    createAction(TOGGLE_ORDER_ITEM_SELECT, orderItemId, { scope });
export const toggleOrderItemDeselect = (orderItemId: string, scope: string) =>
    createAction(TOGGLE_ORDER_ITEM_DESELECT, orderItemId, { scope });
export const togglePaymentSelect = (paymentId: string, scope: string) =>
    createAction(TOGGLE_PAYMENT_SELECT, paymentId, { scope });
export const deselectAll = (scope: string) => createAction(DESELECT_ALL, null, { scope });

export const toggleBillItemsSelect =
    (billId: $TSFixMe, scope: $TSFixMe) => (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();
        const orderItemIds = getOrderItemIdsByBillId(state, { billId });
        const selectableOrderItemIds = getSelectableOrderItemIds(state, { orderItemIds });

        const selectablePaymentIds = getCurrentEnterprisePaymentIdsByBillId(state, { billId });
        return dispatch(
            createAction(TOGGLE_BILL_ITEMS_SELECT, { selectableOrderItemIds, selectablePaymentIds }, { scope })
        );
    };

export const toggleGroupedOrderItemsSelect =
    ({ type, serviceId, serviceOrderId, groupId, billId, ageCategoryId, scope, month }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();

        const orderItemIds = getGroupedOrderItemIds(state, {
            type,
            serviceId,
            serviceOrderId,
            groupId,
            billId,
            ageCategoryId,
            month,
        });
        const selectableOrderItemIds = getSelectableOrderItemIds(state, { orderItemIds });
        return dispatch(
            createAction(TOGGLE_BILL_ITEMS_SELECT, { selectableOrderItemIds, selectablePaymentIds: [] }, { scope })
        );
    };

export const toggleGroupedByServiceOrderItemsSelect =
    ({ serviceId, serviceOrderId, billId, scope, month }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();

        const serviceOrderIds = getNestedProductServiceIds(state, {
            reservationId: serviceOrderId,
            serviceOrderId,
            month,
            billId,
        });
        const serviceIds = getNestedServiceIds(state, { reservationId: serviceOrderId, serviceId, month, billId });

        const orderItemIds = getBillNestedOrderItemIdsByServiceOrderId(state, {
            serviceOrderIds,
            billId,
            month,
            serviceIds,
        });

        const selectableOrderItemIds = getSelectableOrderItemIds(state, { orderItemIds });

        return dispatch(
            createAction(TOGGLE_BILL_ITEMS_SELECT, { selectableOrderItemIds, selectablePaymentIds: [] }, { scope })
        );
    };

export const toggleGroupedByMonthOrderItemsSelect =
    ({ month, billId, scope }: $TSFixMe) =>
    (dispatch: $TSFixMe, getState: $TSFixMe) => {
        const state = getState();

        const selectableOrderItemIds = getOrderItemIdsByMonth(state, {
            month,
            billId,
        });

        return dispatch(
            createAction(TOGGLE_BILL_ITEMS_SELECT, { selectableOrderItemIds, selectablePaymentIds: [] }, { scope })
        );
    };

const initialState = {
    selectedOrderItemIds: {},
    selectedPaymentIds: {},
};

const intersectionNotEmpty = (obj: Record<string, unknown>, comparisonArray: string[]) => {
    for (const key of comparisonArray) {
        if (obj[key]) {
            return true;
        }
    }

    return false;
};

const isDifferenceEmpty = (obj: Record<string, unknown>, comparisonArray: string[]) => {
    let matches = 0;

    for (const key of comparisonArray) {
        if (obj[key]) {
            matches++;
        }
    }

    return matches === comparisonArray.length;
};

const getSelectState = (obj: Record<string, unknown>, comparisonArray: string[]) => {
    let matches = 0;

    for (const key of comparisonArray) {
        if (obj[key]) {
            matches++;
        }
    }

    if (matches === comparisonArray.length) return 'ALL';
    if (matches > 0) return 'SOME';
    return 'NONE';
};

const selectionToStateObject = (array: $TSFixMe) =>
    array.reduce((prev: $TSFixMe, curr: $TSFixMe) => {
        prev[curr] = true;
        return prev;
    }, {});

const handleSelectionChange = (state: Record<string, unknown>, id: string) => {
    if (state[id]) {
        return omit(id, state);
    }
    return set(id, true, state);
};

export const billItemsReducer = createScopedReducer(initialState, {
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [TOGGLE_ORDER_ITEM_SELECT]: (state, orderItemId) => ({
        ...state,
        selectedOrderItemIds: handleSelectionChange(state.selectedOrderItemIds, orderItemId),
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [TOGGLE_ORDER_ITEM_DESELECT]: (state, orderItemId) => ({
        ...state,
        selectedOrderItemIds: omit(orderItemId, state.selectedOrderItemIds),
    }),
    // @ts-expect-error $TSFixMeError: Parameter 'state' implicitly has an 'any' type.
    [TOGGLE_PAYMENT_SELECT]: (state, paymentId) => ({
        ...state,
        selectedPaymentIds: handleSelectionChange(state.selectedPaymentIds, paymentId),
    }),
    [TOGGLE_BILL_ITEMS_SELECT]: (state: $TSFixMe, { selectableOrderItemIds, selectablePaymentIds }: $TSFixMe) => {
        const allOrderItemsSelected = isDifferenceEmpty(state.selectedOrderItemIds, selectableOrderItemIds);
        const allPaymentsSelected = isDifferenceEmpty(state.selectedPaymentIds, selectablePaymentIds);
        const fullSelection = allOrderItemsSelected && allPaymentsSelected;
        const newState = {
            selectedOrderItemIds: {},
            selectedPaymentIds: {},
        };

        if (fullSelection) {
            newState.selectedOrderItemIds = omit(selectableOrderItemIds, state.selectedOrderItemIds);
            newState.selectedPaymentIds = omit(selectablePaymentIds, state.selectedPaymentIds);
        } else {
            const selectedOrderItems = selectionToStateObject(selectableOrderItemIds);
            newState.selectedOrderItemIds = { ...state.selectedOrderItemIds, ...selectedOrderItems };

            const selectedPayments = selectionToStateObject(selectablePaymentIds);
            newState.selectedPaymentIds = { ...state.selectedPaymentIds, ...selectedPayments };
        }

        return newState;
    },
    [DESELECT_ALL]: (state: $TSFixMe) => ({
        ...state,
        selectedOrderItemIds: {},
        selectedPaymentIds: {},
    }),
});

const moduleSelector = createSelector(parentSelector, get(NAME));
const getScope = createScopedSelector({ parentSelector: moduleSelector, initialState });

const getSelectedOrderItemMap = flow(getScope, get('selectedOrderItemIds'));

export const isOrderItemSelected = createSelector(getStringProp('orderItemId'), getSelectedOrderItemMap, has);

const getSelectedPaymentMap = flow(getScope, get('selectedPaymentIds'));

export const isPaymentSelected = createSelector(getStringProp('paymentId'), getSelectedPaymentMap, has);

const getSelectableOrderItemIdsByBillId = composeProps(
    getSelectableOrderItemIds,
    createPropsExtender('orderItemIds', getOrderItemIdsByBillId)
);

export const getSelectStateGroupedByServiceOrderSelected = createSelector(
    getSelectedOrderItemMap,
    getBillNestedOrderItemIdsByServiceOrderId,
    getSelectState
);

export const areAllBillOrderItemsSelected = createSelector(
    getSelectedOrderItemMap,
    getSelectableOrderItemIdsByBillId,
    isDifferenceEmpty
);

const areAllBillPaymentsSelected = createSelector(
    getSelectedPaymentMap,
    getCurrentEnterprisePaymentIdsByBillId,
    isDifferenceEmpty
);

const areSomeBillOrderItemsSelected = createSelector(
    getSelectedOrderItemMap,
    getSelectableOrderItemIdsByBillId,
    intersectionNotEmpty
);

const areSomeBillPaymentsSelected = createSelector(
    getSelectedPaymentMap,
    getCurrentEnterprisePaymentIdsByBillId,
    intersectionNotEmpty
);

export const areSomeBillItemsSelected = createSelector(
    areSomeBillOrderItemsSelected,
    areSomeBillPaymentsSelected,
    (orderItemsSelected, paymentsSelected) => orderItemsSelected || paymentsSelected
);

const getSelectableGroupedOrderItemIds = composeProps(
    getSelectableOrderItemIds,
    createPropsExtender('orderItemIds', getGroupedOrderItemIds)
);

export const getSelectStateGroupedOrderItemsSelected = createSelector(
    getSelectedOrderItemMap,
    getSelectableGroupedOrderItemIds,
    getSelectState
);

const getAllSelectableIdsFromMonth = createSelector(
    getOrderItemIdsByMonth,
    (state: ApplicationState, { month, billId }) => {
        const serviceOrders = getOrderedMonthServiceOrders(state, { billId, month });

        return billId
            ? serviceOrders.flatMap(({ id }) =>
                  getBillPaymentIdsWithServiceOrderId(state, {
                      billId,
                      serviceOrderId: id,
                  })
              )
            : [];
    },
    (orderItemIds, paymentIds) => [...orderItemIds, ...paymentIds]
);

const getAllSelectedIdsFromMonth = createSelector(
    getSelectedOrderItemMap,
    getSelectedPaymentMap,
    (orderItemMap, paymentMap) => ({ ...orderItemMap, ...paymentMap })
);

export const areSomeGroupedByMonthOrderSelected = createSelector(
    getAllSelectedIdsFromMonth,
    getAllSelectableIdsFromMonth,
    intersectionNotEmpty
);

export const areAllGroupedByMonthOrderSelected = createSelector(
    getAllSelectedIdsFromMonth,
    getAllSelectableIdsFromMonth,
    isDifferenceEmpty
);

export const isBillEmpty = createSelector(
    getOrderItemIdsByBillId,
    getCurrentEnterprisePaymentIdsByBillId,
    flow(concat, isEmpty)
);

export const isBillWithNoServiceOrders = flow(
    getOrderItemsByBillId,
    (arr) => arr?.length === undefined || arr.length === 0
);

export const areAllBillItemsSelected = createSelector(
    areAllBillOrderItemsSelected,
    areAllBillPaymentsSelected,
    (orderItemsSelected, paymentsSelected) => orderItemsSelected && paymentsSelected
);

export const getSelectedOrderItemIds = flow(getSelectedOrderItemMap, keys);

export const getSelectedPaymentIds = flow(getSelectedPaymentMap, keys);

export const getAllSelectedIds = createSelector(getSelectedOrderItemIds, getSelectedPaymentIds, concat);

export const isAnythingSelected = createSelector(getAllSelectedIds, notEmpty);

export const getMovableItemsCount = createSelector(getAllSelectedIds, size);

export const getCancelableItems = createSelector(
    composeProps(getOrderItemsByIds, createPropsExtender('ids', getSelectedOrderItemIds)),
    flow(reject({ type: OrderItemType.SpaceOrder }), reject({ accountingState: AccountingState.Canceled }))
);
